import { motion } from "framer-motion";
import { Copy, Clock, Calendar, Trophy, Gift, Gamepad2, Youtube, Twitch, Twitter, Facebook  } from "lucide-react";
import * as React from "react";
import  { type MetaFunction, Link  } from "react-router";
import { Badge } from "#app/components/ui/badge.tsx";
import { Button } from "#app/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "#app/components/ui/card.tsx";
import { Input } from "#app/components/ui/input.tsx";

/**
 * Remix route suggestion: app/routes/subtember2025.tsx
 *
 * QUICK START
 * 1) Update START_TIME below to your actual kickoff (America/Chicago).
 * 2) Adjust follower counts/goals in `platforms`.
 * 3) Replace streaming links in CTA buttons.
 * 4) Fill the schedule and challenge goals.
 */

export const meta: MetaFunction = () => ([
  { title: "Subtember Streamathon 2025 | JaseOwns" },
  { name: "description", content: "Join JaseOwns for the Subtember 2025 Streamathon: live schedule, goals, giveaways, and ways to support across YouTube, Twitch, Discord, X, Facebook, and Reddit." },
  { property: "og:title", content: "Subtember Streamathon 2025 | JaseOwns" },
  { property: "og:description", content: "Live schedule, goals, progress to 5k YouTube subs, and giveaways." },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://jaseowns.com/subtember2025" },
  { name: "twitter:card", content: "summary_large_image" },
]);

const START_TIME = new Date("2025-09-05T18:00:00-05:00"); // TODO: Update to your kickoff (America/Chicago)

function useCountdown(target: Date) {
  const [now, setNow] = React.useState<Date>(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const s = Math.floor(diff / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { days, hours, minutes, seconds, over: diff === 0 };
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="w-full">
      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
        <div
          className="h-3 bg-primary" 
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{value.toLocaleString()} / {max.toLocaleString()} ({pct}%)</div>
    </div>
  );
}

export default function SubtemberLanding() {
  const { days, hours, minutes, seconds } = useCountdown(START_TIME);

  // Editable platform stats & goals
  const platforms: Array<{
    name: string;
    icon: React.ReactNode;
    value: number;
    goal: number;
    url: string;
  }> = [
    { name: "YouTube", icon: <Youtube className="w-4 h-4" />, value: 4948, goal: 5000, url: "https://youtube.com/@jaseowns/live" },
    { name: "Discord", icon: <Youtube className="w-4 h-4" />, value: 2451, goal: 2600, url: "https://discord.gg/jaseowns" },
    { name: "Twitch", icon: <Twitch className="w-4 h-4" />, value: 2424, goal: 2600, url: "https://twitch.tv/jaseowns" },
    { name: "X", icon: <Twitter className="w-4 h-4" />, value: 146, goal: 300, url: "https://x.com/jaseowns" },
    { name: "Facebook", icon: <Facebook className="w-4 h-4" />, value: 128, goal: 300, url: "https://facebook.com/jaseowns" },
    { name: "Reddit", icon: <Youtube className="w-4 h-4" />, value: 10, goal: 100, url: "https://reddit.com/r/jaseowns" },
  ];

  const totalStreams = 548; // Update if needed

  const challenges = [
    { title: "5K YouTube Goal", detail: "Hit 5,000 subs — unlock 5 game key giveaways.", icon: <Trophy className="w-4 h-4" /> },
    { title: "Night Owl", detail: "If we pass midnight CT, I'll play a cursed viewer-picked game.", icon: <Clock className="w-4 h-4" /> },
    { title: "Community Choice", detail: "Top-voted challenge from Discord's #5k-hype.", icon: <Gamepad2 className="w-4 h-4" /> },
    { title: "Milestone Gifts", detail: "Giveaways every milestone: 4,960 • 4,980 • 5,000.", icon: <Gift className="w-4 h-4" /> },
  ];

  const schedule = [
    { when: "Fri Sep 5, 6:00 PM CT", what: "Kickoff + Ultima Online warm-up", length: "3h" },
    { when: "Sat Sep 6, 12:00 PM CT", what: "Community games block + giveaways", length: "4h" },
    { when: "Sat Sep 6, 8:00 PM CT", what: "Challenge night: viewer picks", length: "open" },
    { when: "Sun Sep 7, 1:00 PM CT", what: "Final push to 5K + recap", length: "2h+" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      <header className="sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/50 bg-background/95">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">Subtember 2025</Badge>
            <span className="font-semibold text-foreground">JaseOwns</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm"><a href="#watch-live">Watch Live</a></Button>
            <Button variant="secondary" asChild size="sm"><a href="#schedule">Schedule</a></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-24">
        {/* HERO */}
        <section className="py-12 sm:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight text-foreground">
                Subtember Streamathon <span className="text-primary">2025</span>
              </h1>
              <p className="mt-4 text-foreground/80 text-lg">
                We’re <span className="font-semibold">548 streams</span> deep and pushing to <span className="font-semibold">5,000 YouTube subs</span>. 
                Join the marathon: giveaways, challenges, community games, and late-night chaos.
              </p>

              {/* COUNTDOWN */}
              <Card className="mt-6 border-border/50">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3 text-sm text-foreground/70"><Calendar className="w-4 h-4" /> Kickoff: {START_TIME.toLocaleString("en-US", { dateStyle: "full", timeStyle: "short" })} (CT)</div>
                  <div className="mt-3 grid grid-cols-4 gap-3 text-center">
                    <TimeCell label="Days" value={days} />
                    <TimeCell label="Hours" value={hours} />
                    <TimeCell label="Minutes" value={minutes} />
                    <TimeCell label="Seconds" value={seconds} />
                  </div>
                </CardContent>
              </Card>

              <div id="watch-live" className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg"><a href="https://youtube.com/@jaseowns/live" target="_blank" rel="noreferrer"><Youtube className="w-4 h-4 mr-2"/>Watch on YouTube</a></Button>
                <Button asChild variant="secondary" size="lg"><a href="https://twitch.tv/jaseowns" target="_blank" rel="noreferrer"><Twitch className="w-4 h-4 mr-2"/>Watch on Twitch</a></Button>
                <Button asChild variant="outline" size="lg"><a href="https://discord.gg/" target="_blank" rel="noreferrer"><Youtube className="w-4 h-4 mr-2"/>Join Discord</a></Button>
              </div>
            </motion.div>

            {/* PROGRESS */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-foreground">Milestone Progress</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {platforms.map((p) => (
                    <div key={p.name} className="grid grid-cols-[1fr_auto] gap-3 items-center">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          {p.icon}
                          <a className="hover:underline text-foreground" href={p.url} target="_blank" rel="noreferrer">{p.name}</a>
                        </div>
                        <div className="mt-2">
                          <ProgressBar value={p.value} max={p.goal} />
                        </div>
                      </div>
                      <Badge variant={p.name === "YouTube" ? "default" : "secondary"}>
                        {p.goal.toLocaleString()} goal
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="mt-4 border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-foreground">Stream Count</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">{totalStreams.toLocaleString()} streams completed</div>
                  <p className="text-sm text-foreground/70">Thanks for powering this journey — let’s make Subtember the biggest yet.</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* GOALS & CHALLENGES */}
        <section className="py-8" id="goals">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold text-foreground">Goals & Unlocks</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {challenges.map((c) => (
              <Card key={c.title} className="h-full border-border/50 hover:border-border/80 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-foreground">{c.icon}{c.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-foreground/80">{c.detail}</CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* SCHEDULE */}
        <section className="py-8" id="schedule">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold text-foreground">Schedule</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {schedule.map((s, i) => (
              <Card key={i} className="border-border/50 hover:border-border/80 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-foreground">{s.when}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="font-medium text-foreground">{s.what}</div>
                  <div className="text-sm text-foreground/70 mt-1">Estimated: {s.length}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* SHARE LINK */}
        <section className="py-8" id="share">
          <Card className="border-border/50">
            <CardHeader className="pb-2">
                                <CardTitle className="text-base text-foreground">Share this event</CardTitle>
            </CardHeader>
            <CardContent>
              <ShareBlock />
            </CardContent>
          </Card>
        </section>

        {/* FOOTER CTA */}
        <section className="pt-8" id="cta">
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
            <Button asChild size="lg"><a href="https://youtube.com/@jaseowns/live" target="_blank" rel="noreferrer"><Youtube className="w-4 h-4 mr-2"/>Open YouTube Live</a></Button>
            <Button asChild variant="secondary" size="lg"><a href="https://twitch.tv/jaseowns" target="_blank" rel="noreferrer"><Twitch className="w-4 h-4 mr-2"/>Open Twitch</a></Button>
            <div className="text-sm text-foreground/70">Follow for updates: </div>
            <div className="flex flex-wrap gap-2">
              <SocialLink href="https://discord.gg/jaseowns" icon={<Youtube className="w-4 h-4" />} label="Discord" />
              <SocialLink href="https://x.com/jaseowns" icon={<Twitter className="w-4 h-4" />} label="X" />
              <SocialLink href="https://facebook.com/jaseowns" icon={<Facebook className="w-4 h-4" />} label="Facebook" />
              <SocialLink href="https://reddit.com/r/jaseowns" icon={<Youtube className="w-4 h-4" />} label="Reddit" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/50 py-6 text-center text-xs text-foreground/70">
        © {new Date().getFullYear()} JaseOwns • Subtember 2025
      </footer>
    </div>
  );
}

function TimeCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-3 rounded-xl bg-muted/50 border border-border/50 hover:bg-muted/70 transition-colors">
      <div className="text-2xl font-bold tabular-nums text-foreground">{value.toString().padStart(2, "0")}</div>
      <div className="text-xs uppercase tracking-wide text-foreground/70">{label}</div>
    </div>
  );
}

function SocialLink({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border/50 hover:bg-muted/50 hover:border-border/80 transition-colors text-foreground">
      {icon}
      <span className="text-sm">{label}</span>
    </a>
  );
}

function ShareBlock() {
  const [url, setUrl] = React.useState("https://jaseowns.com/subtember2025");
  const [copied, setCopied] = React.useState(false);
  const shareText = encodeURIComponent("Join me for the Subtember 2025 Streamathon! 🎉 Schedule, goals & giveaways:");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="grid gap-3 md:grid-cols-[1fr_auto] items-center">
      <div className="flex items-center gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} className="border-border/50" />
        <Button variant="secondary" onClick={handleCopy}><Copy className="w-4 h-4 mr-1" /> {copied ? "Copied" : "Copy"}</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline"><a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(url)}` }><Twitter className="w-4 h-4 mr-2"/>Share on X</a></Button>
        <Button asChild variant="outline"><a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}><Facebook className="w-4 h-4 mr-2"/>Share</a></Button>
        <Button asChild variant="outline"><a target="_blank" rel="noreferrer" href={`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent("Subtember Streamathon 2025 | JaseOwns")}`}><Youtube className="w-4 h-4 mr-2"/>Post</a></Button>
      </div>
    </div>
  );
}
